import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  SIEM_GROUPS_KEY,
  createSiemGroup,
  removeSiemGroup,
  setCompanySiemGroup,
  setSiemGroupState,
  siemGroupCompaniesQuery,
  siemGroupHistoryQuery,
  siemGroupsQuery,
  type SiemGroupCompany,
  type SiemGroupHistoryRow,
  type SiemGroupRow,
} from '../../api/securiteeSiemGroups'
import { Spinner } from '../../ui/Spinner'
import { WazuhConnectionSection } from './WazuhConnectionSection'

/**
 * Settings > Securitee > XDR > SIEM groups: fiecare grup are conexiunea lui
 * Wazuh; o firma de varf sta intr-un singur grup, subsidiarele urmeaza.
 * Cine nu vede niciun grup primeste 403 si sectiunea nu se deseneaza.
 */

const CONFIRM = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 } as const
const TH = { textAlign: 'left', fontWeight: 600 } as const

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.'
}

export function SiemGroupsSection() {
  const client = useQueryClient()
  const page = useQuery(siemGroupsQuery())
  const [openId, setOpenId] = useState(1)
  // numele grupului abia facut, pana il aduce lista
  const [createdName, setCreatedName] = useState('')

  if (page.error instanceof ApiError && page.error.status === 403) return null
  if (page.error) return <div className="alert alert-danger">Something went wrong: {errorText(page.error)}</div>
  if (!page.data) return <Spinner />

  const { groups, unassigned, can_admin: admin } = page.data
  const open = groups.find((g) => g.ID === openId) ?? (openId === 1 ? groups[0] : undefined)
  const refresh = () => client.invalidateQueries({ queryKey: SIEM_GROUPS_KEY })

  return (
    <div className="xdr-siem-groups" style={{ fontSize: 13 }}>
      <p className="help-block" style={{ marginTop: 0, maxWidth: 720 }}>
        Each SIEM group has its own Wazuh connection. A company sends its events to the group it is in; its subsidiaries follow it. Shared Class is the
        Wazuh most companies use.
      </p>

      <div style={{ overflowX: 'auto', position: 'relative' }}>
        <table className="table table-condensed xdr-siem-groups-table" style={{ marginBottom: 10 }}>
          <thead>
            <tr className="text-muted" style={{ fontSize: 12 }}>
              <th style={TH}>Group</th>
              <th style={TH}>State</th>
              <th style={TH}>Companies</th>
              <th style={TH}>Last sent</th>
              <th style={TH}>Last read</th>
              <th style={TH}>
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.ID} className={g.ID === open?.ID ? 'info' : undefined}>
                <td>
                  <strong>{g.name}</strong>
                </td>
                <td>{g.disabled ? <span className="label label-default">Off</span> : <span className="label label-success">On</span>}</td>
                <td>{g.companies}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{g.last_forward_at ?? 'Not yet'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{g.last_alert_read_at ?? 'Not yet'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button type="button" className="btn btn-default btn-xs" aria-label={`Open ${g.name}`} disabled={g.ID === open?.ID} onClick={() => setOpenId(g.ID)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {admin && (
        <AddGroup
          onCreated={(id, name) => {
            setCreatedName(name)
            setOpenId(id)
            void refresh()
          }}
        />
      )}

      {admin && unassigned.length > 0 && <Unassigned companies={unassigned} groups={groups} onDone={refresh} />}

      {(open || openId !== 1) && (
        <GroupPanel
          key={openId}
          group={open ?? { ID: openId, name: createdName, owner_sp: null, disabled: 0, companies: 0, last_forward_at: null, last_alert_read_at: null, can_edit: true }}
          groups={groups}
          admin={admin}
          onChanged={refresh}
          onRemoved={() => {
            setOpenId(1)
            void refresh()
          }}
        />
      )}
    </div>
  )
}

function AddGroup({ onCreated }: { onCreated: (id: number, name: string) => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const out = await createSiemGroup(name.trim())
      setName('')
      onCreated(out.group, name.trim())
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className={'xdr-siem-add' + (error ? ' has-error' : '')} onSubmit={(e) => void submit(e)} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 6 }}>
      <input
        className="form-control input-sm"
        aria-label="New SIEM group name"
        placeholder="New SIEM group name"
        maxLength={100}
        value={name}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        style={{ width: 260, maxWidth: '100%' }}
      />
      <button type="submit" className="btn btn-default btn-sm" disabled={busy || name.trim() === ''} aria-label="Add SIEM group">
        <i className="fas fa-plus" aria-hidden="true"></i> Add group
      </button>
      {error && (
        <span className="help-block" style={{ flexBasis: '100%', margin: 0 }}>
          {error}
        </span>
      )}
    </form>
  )
}

/** Firmele de varf fara grup: XDR nu porneste pe ele, evenimentele lor nu pleaca. */
function Unassigned({ companies, groups, onDone }: { companies: { ID: number; name: string }[]; groups: SiemGroupRow[]; onDone: () => void }) {
  const [choice, setChoice] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState('')
  const targets = groups.filter((g) => !g.disabled)

  const assign = async (company: number) => {
    setBusy(company)
    setError('')
    try {
      await setCompanySiemGroup(company, Number(choice[company]))
      onDone()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="alert alert-warning xdr-siem-unassigned" aria-label="Companies without a SIEM group" style={{ marginTop: 14 }}>
      <strong>Companies without a SIEM group</strong>
      <p style={{ margin: '4px 0 8px' }}>XDR cannot be turned on for these companies and their events are not sent until they get a SIEM group.</p>
      {error && <div className="text-danger">{error}</div>}
      <ul className="list-unstyled" style={{ margin: 0 }}>
        {companies.map((c) => (
          <li key={c.ID} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <span style={{ minWidth: 160 }}>{c.name}</span>
            <select
              className="form-control input-sm"
              aria-label={`SIEM group for ${c.name}`}
              value={choice[c.ID] ?? ''}
              onChange={(e) => setChoice((all) => ({ ...all, [c.ID]: e.target.value }))}
              style={{ width: 'auto', maxWidth: '100%' }}
            >
              <option value="">Choose a SIEM group…</option>
              {targets.map((g) => (
                <option key={g.ID} value={g.ID}>
                  {g.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary btn-sm" aria-label={`Assign ${c.name}`} disabled={!choice[c.ID] || busy !== null} onClick={() => void assign(c.ID)}>
              Assign
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function GroupPanel({
  group,
  groups,
  admin,
  onChanged,
  onRemoved,
}: {
  group: SiemGroupRow
  groups: SiemGroupRow[]
  admin: boolean
  onChanged: () => void
  onRemoved: () => void
}) {
  const [confirm, setConfirm] = useState<'disable' | 'remove' | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const shared = group.ID === 1

  const act = async (what: 'disable' | 'enable' | 'remove') => {
    setBusy(true)
    setError('')
    try {
      if (what === 'remove') {
        await removeSiemGroup(group.ID)
        onRemoved()
      } else {
        await setSiemGroupState(group.ID, what)
        onChanged()
      }
      setConfirm(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="xdr-siem-group" aria-label={`SIEM group ${group.name}`} style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid #e3e8ee' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <h5 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{group.name}</h5>
        {group.disabled ? <span className="label label-default">Off</span> : null}
        {admin && !shared && (
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto' }}>
            {group.disabled ? (
              <button type="button" className="btn btn-default btn-xs" aria-label={`Turn on ${group.name}`} disabled={busy} onClick={() => void act('enable')}>
                Turn on
              </button>
            ) : (
              <button type="button" className="btn btn-default btn-xs" aria-label={`Turn off ${group.name}`} disabled={busy} onClick={() => setConfirm('disable')}>
                Turn off
              </button>
            )}
            {group.companies === 0 && (
              <button type="button" className="btn btn-default btn-xs" aria-label={`Remove ${group.name}`} disabled={busy} onClick={() => setConfirm('remove')}>
                Remove
              </button>
            )}
          </span>
        )}
      </div>

      {confirm && (
        <div className="alert alert-danger xdr-siem-confirm" style={CONFIRM}>
          <span>
            {confirm === 'disable'
              ? 'Nothing is sent to this Wazuh while it is off: the events of its companies wait and go out when it is turned on again.'
              : 'The group and its connection are removed. It has no companies.'}
          </span>
          <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => void act(confirm)}>
            {confirm === 'disable' ? 'Yes, turn it off' : 'Yes, remove it'}
          </button>
          <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setConfirm(null)}>
            Cancel
          </button>
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      <div style={{ marginTop: 12 }}>
        <WazuhConnectionSection group={group.ID} heading={false} />
      </div>

      <GroupCompanies group={group} groups={groups} admin={admin} onMoved={onChanged} />
      <GroupHistory group={group.ID} />
    </section>
  )
}

function GroupCompanies({ group, groups, admin, onMoved }: { group: SiemGroupRow; groups: SiemGroupRow[]; admin: boolean; onMoved: () => void }) {
  const client = useQueryClient()
  const list = useQuery(siemGroupCompaniesQuery(group.ID))
  const [target, setTarget] = useState<Record<number, string>>({})
  const [confirm, setConfirm] = useState<SiemGroupCompany | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const others = groups.filter((g) => g.ID !== group.ID && !g.disabled)

  const move = async (company: SiemGroupCompany) => {
    setBusy(true)
    setError('')
    try {
      await setCompanySiemGroup(company.ID, Number(target[company.ID]))
      setConfirm(null)
      await client.invalidateQueries({ queryKey: SIEM_GROUPS_KEY })
      onMoved()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const nameOf = (id: string | undefined) => groups.find((g) => String(g.ID) === id)?.name ?? ''
  const companies = list.data?.companies ?? []

  return (
    <div className="xdr-siem-companies" style={{ marginTop: 16 }}>
      <strong>Companies in this group</strong>
      {list.isPending && <Spinner />}
      {list.error && <div className="alert alert-danger">Something went wrong: {errorText(list.error)}</div>}
      {list.data && companies.length === 0 && <p className="text-muted" style={{ margin: '4px 0 0' }}>No company is in this group yet.</p>}
      {error && <div className="alert alert-danger">{error}</div>}
      <ul className="list-unstyled" style={{ margin: '4px 0 0' }}>
        {companies.map((c) => (
          <li key={c.ID} style={{ padding: '6px 0', borderBottom: '1px solid #f0f2f5' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span style={{ minWidth: 160 }}>{c.name}</span>
              {c.set_at && (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  since {c.set_at}
                  {c.set_by_name ? `, by ${c.set_by_name}` : ''}
                </span>
              )}
              {admin && others.length > 0 && (
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, marginLeft: 'auto' }}>
                  <select
                    className="form-control input-sm"
                    aria-label={`Move ${c.name} to`}
                    value={target[c.ID] ?? ''}
                    onChange={(e) => setTarget((all) => ({ ...all, [c.ID]: e.target.value }))}
                    style={{ width: 'auto', maxWidth: '100%' }}
                  >
                    <option value="">Move to…</option>
                    {others.map((g) => (
                      <option key={g.ID} value={g.ID}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-default btn-sm" aria-label={`Move ${c.name}`} disabled={!target[c.ID] || busy} onClick={() => setConfirm(c)}>
                    Move
                  </button>
                </span>
              )}
            </div>
            {confirm?.ID === c.ID && (
              <div className="alert alert-danger xdr-siem-confirm" style={CONFIRM}>
                <span>
                  From now on the events of {c.name} and its subsidiaries go to {nameOf(target[c.ID])}. What was already sent stays in {group.name}.
                </span>
                <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => void move(c)}>
                  Yes, move it
                </button>
                <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setConfirm(null)}>
                  Cancel
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function historyText(row: SiemGroupHistoryRow): string {
  switch (row.action) {
    case 'created':
      return `Created as ${row.new_value ?? ''}`
    case 'disabled':
      return 'Turned off'
    case 'enabled':
      return 'Turned on'
    case 'removed':
      return `Removed ${row.old_value ?? ''}`
    case 'connection':
      return `Connection: ${row.new_value ?? ''}`
    case 'webhook':
      return `Webhook: ${row.new_value ?? ''}`
    case 'company_set':
      return `${row.company_name ?? 'A company'}: ${row.old_value ?? 'no group'} → ${row.new_value ?? ''}`
    default:
      return row.action
  }
}

/** Istoricul se cere numai cand e deschis. */
function GroupHistory({ group }: { group: number }) {
  const [open, setOpen] = useState(false)
  const history = useQuery({ ...siemGroupHistoryQuery(group), enabled: open })
  const rows = history.data?.history ?? []

  return (
    <div className="xdr-siem-history" style={{ marginTop: 14 }}>
      <button type="button" className="btn btn-link btn-sm" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ padding: 0 }}>
        <i className={open ? 'fas fa-chevron-down' : 'fas fa-chevron-right'} aria-hidden="true"></i> History
      </button>
      {open && (
        <>
          {history.isPending && <Spinner />}
          {history.error && <div className="alert alert-danger">Something went wrong: {errorText(history.error)}</div>}
          {history.data && rows.length === 0 && <p className="text-muted">Nothing changed yet.</p>}
          <ul className="list-unstyled" style={{ margin: '6px 0 0', fontSize: 12 }}>
            {rows.map((r, i) => (
              <li key={i} style={{ padding: '3px 0' }}>
                <span className="text-muted">
                  {r.created_at}
                  {r.user_name ? ` · ${r.user_name}` : ''}
                </span>{' '}
                <span style={{ overflowWrap: 'anywhere' }}>{historyText(r)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
